import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/**
 * Module card for the operational dashboard (per-phase grid).
 * status: "complete" | "in-progress" | "ready" | "dim"
 */
export default function PortalModuleCard({ letter, title, summary, status, fileCount, onClick, index }) {
    const isDim = status === "dim";
    const statusLabel = {
        complete: "Complete",
        "in-progress": "In Progress",
        ready: "Ready",
        dim: "Advanced",
    }[status] || "Ready";

    const statusColor = {
        complete: "text-tca-cyan-hi border-tca-cyan/70",
        "in-progress": "text-tca-cyan border-tca-cyan/50",
        ready: "text-tca-ink-muted border-tca-ink-dim",
        dim: "text-tca-ink-muted/50 border-tca-ink-dim/40",
    }[status] || "text-tca-cyan";

    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: isDim ? 0.6 : 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.04 * index }}
            whileHover={{ y: isDim ? 0 : -2 }}
            onClick={onClick}
            disabled={isDim}
            data-testid={`portal-module-${letter}`}
            data-status={status}
            className={`
                group relative w-full text-left
                border ${isDim ? "border-tca-ink-dim/40" : "border-tca-cyan/22 hover:border-tca-cyan/55"}
                bg-tca-bg-panel/60 backdrop-blur-md p-5
                transition-all duration-300
                ${isDim ? "cursor-default" : "hover:bg-tca-bg-panel/80 hover:shadow-[inset_0_0_22px_rgba(0,219,255,0.08)]"}
            `}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-baseline gap-2.5">
                    <span className={`font-mono-tight font-bold text-2xl ${isDim ? "text-tca-ink-muted/40" : "text-tca-cyan-hi"}`}>
                        {letter}
                    </span>
                    <span className={`font-display text-base leading-tight ${isDim ? "text-tca-ink-muted/50" : "text-tca-ink"}`}>
                        {title}
                    </span>
                </div>
                {!isDim && <ArrowUpRight size={14} className="text-tca-ink-muted group-hover:text-tca-cyan-hi transition-colors flex-shrink-0" />}
            </div>

            <p className={`mt-2 text-xs leading-snug ${isDim ? "text-tca-ink-muted/40" : "text-tca-ink-muted"}`}>
                {summary}
            </p>

            <div className="mt-4 flex items-center justify-between gap-3">
                <span className={`font-mono-tight text-[9px] uppercase tracking-[0.24em] px-2 py-1 border rounded-full ${statusColor}`}>
                    {statusLabel}
                </span>
                {fileCount > 0 && (
                    <span className="font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted">
                        {fileCount} {fileCount === 1 ? "file" : "files"}
                    </span>
                )}
            </div>
        </motion.button>
    );
}
